import React, { useState, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { Container } from 'react-bootstrap';
import { RiArrowDropDownLine, RiArrowDropUpLine } from "react-icons/ri";
import { Link, useNavigate } from 'react-router-dom';
import itunes from '../assets/img/itunes.png';
import netflix from '../assets/img/netflix.png';
import prime from '../assets/img/prime.png';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';

const CircularCarousel = ({ items, title }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setDirection] = useState(0);

  const rotate = useCallback((newDirection) => {
    setDirection(newDirection);
    setActiveIndex((prev) => (prev + newDirection + items.length) % items.length);
  }, [items.length]);

  const handleControlClick = (e, callback) => {
    e.stopPropagation();
    callback();
  };

  const handleItemClick = (url) => {
    if (typeof url === 'function') {
      url();
    }
  };

  return (
    <div className="w-full flex flex-col items-center py-6" role="region" aria-label={`${title} carousel`}>
      <div className="relative w-full max-w-2xl flex items-center justify-center">
        <motion.button
          className="absolute left-0 z-10 w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full hover:bg-gray-700"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => handleControlClick(e, () => rotate(-1))}
          aria-label="Previous slide"
        >
          <FaArrowLeft className="text-white text-xl" />
        </motion.button>

        <div className="w-3/4 mx-auto">
          <AnimatePresence initial={false}>
            {items.map((item, index) => (
              <motion.div
                key={item.id}
                className={`${index === activeIndex ? 'block' : 'hidden'}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                onClick={() => handleItemClick(item.url)}
              >
                <img 
                  src={item.img} 
                  alt={item.title} 
                  className="w-full h-auto rounded-lg shadow-xl cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <motion.button
          className="absolute right-0 z-10 w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full hover:bg-gray-700"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => handleControlClick(e, () => rotate(1))}
          aria-label="Next slide"
        >
          <FaArrowRight className="text-white text-xl" />
        </motion.button>
      </div>

      <div className="flex justify-center space-x-2 mt-4">
        {items.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-colors ${
              index === activeIndex ? 'bg-yellow-400' : 'bg-gray-600'
            }`}
            onClick={(e) => handleControlClick(e, () => setActiveIndex(index))}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Distributions = () => {
  let navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [featuredDistributions, setFeaturedDistributions] = useState([]);

  const toggleDropdown = (dropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const defaultDistributions = [
    {
      id: 1,
      title: 'Netflix',
      img: netflix,
      url: () => navigate('/netflix'),
    },
    {
      id: 2,
      title: 'Prime',
      img: prime,
      url: () => navigate('/prime'),
    },
    {
      id: 3,
      title: 'Itunes',
      img: itunes,
      url: () => navigate('/itunes'),
    },
  ];

  useEffect(() => {
    fetch(`${baseUrl}/featured_distributions`)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(setFeaturedDistributions)
      .catch((error) => {
        setFeaturedDistributions(defaultDistributions);
      });
  }, []);

  return (
    <>
      <SEO
        title="Film Distribution"
        description="Distribute your film across major streaming platforms worldwide using ProducerBuddy's integrated distribution tools."
        url="https://producerbuddy.pilomation.com/distributions"
        robots="noindex, nofollow"
      />
      <SubNav />
      <div className='bg-gray-900 text-white min-h-screen p-4'>
        <div className="max-w-7xl mx-auto">
          <h2 className='font-extrabold text-center p-4 text-yellow-400 text-4xl'>
            Connect with Distributors Who Value Your Movie
          </h2>
          <p className='text-center text-lg p-4 max-w-3xl mx-auto text-gray-300'>
            Explore various distribution channels and connect with the right distributors for your movie. Whether it's digital, theatrical, or TV broadcast, we have options for you.
          </p>

          {/* First Row of Distribution Categories */}
          <div className='flex lg:flex-row justify-center items-stretch gap-4 py-8'>
            <div className='bg-gray-800 shadow-lg p-5 rounded-lg w-1/2 lg:w-1/2 cursor-pointer' onClick={() => toggleDropdown('digital')}>
              <div className='flex justify-between items-center'>
                <p className='text-xl font-semibold'>Digital Distributions</p>
                {openDropdown === 'digital' ? <RiArrowDropUpLine size={30} /> : <RiArrowDropDownLine size={30} />}
              </div>
              {openDropdown === 'digital' && (
                <div className="mt-4">
                  <p className="text-gray-300 mb-4">Digital distribution offers opportunities to showcase your movie on platforms like Netflix, Amazon Prime, or iTunes.</p>
                  <CircularCarousel
                    items={featuredDistributions.length > 0 ? featuredDistributions : defaultDistributions}
                    title="Digital Distributions"
                  />
                </div>
              )}
            </div>

            <div className='bg-gray-800 shadow-lg p-5 rounded-lg w-1/2 lg:w-1/2 cursor-pointer' onClick={() => toggleDropdown('theatrical')}>
              <div className='flex justify-between items-center'>
                <p className='text-xl font-semibold'>Theatrical Distributions</p>
                {openDropdown === 'theatrical' ? <RiArrowDropUpLine size={30} /> : <RiArrowDropDownLine size={30} />}
              </div>
              {openDropdown === 'theatrical' && (
                <div className="mt-4 text-gray-300">
                  <p>Get your movie on the big screen. We connect you with distributors who specialize in theatrical releases worldwide.</p>
                </div>
              )}
            </div>
          </div>

          {/* Second Row of Distribution Categories */}
          <div className='flex lg:flex-row justify-center items-stretch gap-4 py-4'>
            <div className='bg-gray-800 shadow-lg p-5 rounded-lg w-1/3 lg:w-1/3 cursor-pointer' onClick={() => toggleDropdown('vod')}>
              <div className='flex justify-between items-center'>
                <p className='text-xl font-semibold'>Video On Demand</p>
                {openDropdown === 'vod' ? <RiArrowDropUpLine size={30} /> : <RiArrowDropDownLine size={30} />}
              </div>
              {openDropdown === 'vod' && (
                <div className="mt-4 text-gray-300">
                  <p>Distribute your movie through video-on-demand (VOD) platforms like YouTube, Vimeo, and others.</p>
                </div>
              )}
            </div>

            <div className='bg-gray-800 shadow-lg p-5 rounded-lg w-1/3 lg:w-1/3 cursor-pointer' onClick={() => toggleDropdown('screening')}>
              <div className='flex justify-between items-center'>
                <p className='text-xl font-semibold'>Distributor Screening</p>
                {openDropdown === 'screening' ? <RiArrowDropUpLine size={30} /> : <RiArrowDropDownLine size={30} />}
              </div>
              {openDropdown === 'screening' && (
                <div className="mt-4 text-gray-300">
                  <p>Arrange private screenings with potential distributors to showcase your movie in a professional setting.</p>
                </div>
              )}
            </div>

            <div className='bg-gray-800 shadow-lg p-5 rounded-lg w-1/3 lg:w-1/3 cursor-pointer' onClick={() => toggleDropdown('broadcast')}>
              <div className='flex justify-between items-center'>
                <p className='text-xl font-semibold'>TV Broadcast</p>
                {openDropdown === 'broadcast' ? <RiArrowDropUpLine size={30} /> : <RiArrowDropDownLine size={30} />}
              </div>
              {openDropdown === 'broadcast' && (
                <div className="mt-4 text-gray-300">
                  <p>Get your movie on TV channels across the globe, reaching millions of viewers through licensed TV broadcasting.</p>
                </div>
              )}
            </div>
          </div>

          {/* Call to Action */}
          <div className='text-center py-8'>
            <Link 
              to='/connect' 
              className='bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold px-8 py-3 rounded-lg shadow-lg transition-colors'
            >
              Get Connected with Distributors
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default Distributions;
